import type { PatientAppointment } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import type { Column } from '@/components/ui/data-table';
import { DataTable } from '@/components/ui/data-table';
import { DataTableSkeleton, EmptyState, ErrorState } from '@/components/ui/state-views';
import { StatusBadge } from '@/components/ui/status-badge';
import { getPatientAppointments } from '@/config/api-helpers';
import { formatDate } from '@/utils/format';

const STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'bg-blue-50 text-blue-700',
  OCCURRED: 'bg-green-50 text-green-700',
  CANCELLED: 'bg-neutral-100 text-neutral-600',
  NO_SHOW: 'bg-red-50 text-red-700',
  RE_SCHEDULED: 'bg-amber-50 text-amber-700',
};

function formatType(type: string | null) {
  if (type == null) return '\u2014';
  return type.replace(/_/g, ' ');
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function AppointmentsTab({ patientId }: { patientId: number }) {
  const navigate = useNavigate();
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['patient-appointments', patientId],
    queryFn: () => getPatientAppointments(patientId),
  });

  if (error)
    return <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='Appointments are unavailable' />;
  if (isLoading) return <DataTableSkeleton columns={8} label='Loading appointments' />;
  if (!data || data.length === 0) {
    return <EmptyState message='No appointments found for this patient.' />;
  }

  const columns: Column<PatientAppointment>[] = [
    { key: 'date', header: 'Date', render: (a) => <span className='text-neutral-700'>{formatDate(a.date)}</span> },
    {
      key: 'time',
      header: 'Time',
      render: (a) => (
        <span className='whitespace-nowrap text-neutral-700'>
          {formatTime(a.start)} – {formatTime(a.end)}
        </span>
      ),
    },
    { key: 'title', header: 'Title', render: (a) => <span className='text-neutral-900'>{a.title ?? '\u2014'}</span> },
    {
      key: 'type',
      header: 'Type',
      render: (a) => <span className='text-neutral-700'>{formatType(a.appointmentType)}</span>,
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (a) => <span className='text-neutral-700'>{formatType(a.contactType)}</span>,
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (a) => (
        <button
          className='text-neutral-700 underline decoration-neutral-300 hover:text-neutral-900'
          onClick={() => navigate(`/providers/${a.provider.providerId}`)}
          type='button'
        >
          {a.provider.firstName} {a.provider.lastName}
        </button>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) =>
        a.status ? (
          <StatusBadge status={a.status} colors={STATUS_COLORS} />
        ) : (
          <span className='text-neutral-500'>—</span>
        ),
    },
    {
      key: 'length',
      header: 'Length',
      render: (a) => <span className='text-neutral-700'>{a.length != null ? `${a.length} min` : '\u2014'}</span>,
    },
  ];

  return <DataTable columns={columns} data={data} keyFn={(a) => a.id} />;
}
